/* ==========================================================================
   gt-search-keep-open.js
   Goldtop / PDP v2 preview theme. Written 13 Sep, corrected the same day
   against a live measurement - see MEASURED below, it is the whole story.

   THE COMPLAINT (Joe, 13 Sep, item 4):
   "For the site search bar, when you type a few characters, but then delete
    them all, the search closes. Please keep it open with no characters so
    that a user can keep searching if (for example) they started to spell
    something and mis-spelt it."

   That is a real abandonment path. A shopper mistypes "jaket", backspaces to
   fix it, and the drawer shuts under their fingers.

   MEASURED, on goldtop.co.uk at 375px, before any fix:
     opened          ->  predictive-search class="is-active", input focused
     typed "jak"     ->  is-active, 3770 bytes of results, input focused
     emptied the box ->  class="", results 0, input NOT focused
   So emptying the query makes the theme strip is-active AND blur the input.
   Both halves matter: the drawer closes and the caret leaves the box, which
   is why the shopper cannot simply keep typing.

   WHY THIS IS A SEPARATE FILE AND NOT A THEME.JS EDIT.
   The behaviour lives in assets/theme.js - 262,548 bytes, and the only copy
   this session can reach is the CDN URL, which the sandbox cannot fetch. So
   theme.js cannot be read, and a file that cannot be read must not be
   rewritten. Same constraint that produced gt-pdp-v2-x.js, same answer:
   leave the big file alone and take the event before it gets there.

   HOW IT WORKS.
   Impulse's <predictive-search> binds an input handler that, on an empty
   query, calls its own close(). We listen on document in the CAPTURE phase,
   which by definition runs before any listener bound on the input or on the
   element itself, whatever order the theme's scripts ran in. When the query is
   empty we stopPropagation(), so the theme's handler never sees the event and
   never calls close(). The drawer stays open, the caret stays in the box.

   Blocking that event also means the theme never clears its own results, so we
   clear them ourselves - otherwise the panel would sit there showing hits for
   a query that is no longer in the box.

   THE CORRECTION, AND THE LESSON IN IT.
   The first draft of clearResults() removed is-active along with is-loading,
   on the assumption that is-active meant "results are showing". The
   measurement above says it does not: is-active IS the open state, present the
   moment the drawer opens and before a single character is typed. Removing it
   would have closed the drawer by hand - the exact bug, reimplemented. It is
   only caught because the before-state was measured rather than assumed, which
   is the rule this project keeps re-learning.

   WHAT IT DELIBERATELY DOES NOT TOUCH.
   - Escape, the close button, and clicking the screen. Those are the ways a
     shopper says "I am done", and they still close it.
   - Any non-empty query. Verified: retyping after an empty box returns 8323
     bytes of fresh results, so the normal search path is untouched.
   - The /search results page, which is a different form entirely.

   It asserts no class name of its own. The only class it removes is
   is-loading, and the only one it reads is the input's name. It never sets the
   open state, because it never needs to - it stops the thing that would have
   changed it. That is what makes it safe to ship against a file we cannot
   read.
   ========================================================================== */
(function () {
  "use strict";

  function host(el) {
    return el && el.closest ? el.closest("predictive-search") : null;
  }

  /* Is this the predictive-search query box, and is it now empty? */
  function emptyQuery(t) {
    if (!t || t.tagName !== "INPUT") return null;
    if (t.name !== "q") return null;
    var ps = host(t);
    if (!ps) return null;
    return t.value.trim() === "" ? ps : null;
  }

  /* Empty the results panel and leave everything else exactly where it was.
     is-active is NOT touched here - see THE CORRECTION above. */
  function clearResults(ps) {
    var panel = ps.querySelector("#predictive-search, .search__results");
    if (panel) panel.innerHTML = "";
    var input = ps.querySelector('input[name="q"]');
    if (input) input.setAttribute("aria-expanded", "false");
    ps.classList.remove("is-loading");
    ps.removeAttribute("loading");
    /* The theme puts this on <body> while results are showing and takes it off
       when they go. We blocked the handler that would have taken it off. */
    document.body.classList.remove("predictive-overflow-hidden");
  }

  document.addEventListener(
    "input",
    function (e) {
      var ps = emptyQuery(e.target);
      if (!ps) return;
      e.stopPropagation();
      clearResults(ps);
    },
    true
  );

  /* A held backspace can empty the box on keyup without a further input event
     in some engines, and some builds listen on keyup as well as input. Same
     guard, same reason. Escape is let through so it still closes. */
  document.addEventListener(
    "keyup",
    function (e) {
      if (e.key === "Escape") return;
      if (!emptyQuery(e.target)) return;
      e.stopPropagation();
    },
    true
  );
})();
